import { Button, Dialog, DialogContent, Grid, Stack, TextField } from '@mui/material';
import { ErrorMessage, Field, Form, Formik, FormikErrors, FormikHelpers, FormikValues } from 'formik';
import { useSnackbar } from 'notistack';
import React from 'react';

import { useCreateUpdateScores, useGetScoresByAssessmentId } from '@/api';
import { Loader, SelectFormField, UIDialogTitle } from '@/shared/components';
import { globalTypes } from '@/shared/duck';
import { useAssessmentsContext } from '@/shared/providers';

import { ActivityTable } from './components';
import { ScoreFormValues, validationSchema } from './duck';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const ScoreUpdateModal: React.FC<Props> = ({
  isOpen,
  onClose,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { selectedAssessment } = useAssessmentsContext();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { scores, isScoresLoading, isScoresError } = useGetScoresByAssessmentId(selectedAssessment?.id || '');
  const { createUpdateScore, isActionLoading } = useCreateUpdateScores();

  const scoresArray = React.useMemo(() => Array.isArray(scores) ? scores : [], [scores]);

  const activityIdOptions: globalTypes.FormSelectFieldOptionType[] = React.useMemo(() => {
    if (!scoresArray.length) {
      return [];
    }

    return scoresArray.map(score => (
      {
        id: score.std_id,
        name: score.std_id,
      }
    ));
  }, [scoresArray]);

  const activityIdMappings = React.useMemo(() => {
    const mappings: Record<string, any> = {};

    scoresArray.forEach(score => {
      if (score && score.std_id) {
        mappings[score.std_id] = score;
      }
    });

    return mappings;
  }, [scoresArray]);


  const initialFormValues: ScoreFormValues = {
    activityId: '',
    score: 0,
    comment: '',
    evidence: '',
  };

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async (values: ScoreFormValues, { resetForm }: FormikHelpers<ScoreFormValues>) => {
    setIsSubmitting(true);

    try {
      await createUpdateScore({
        assessmentId: selectedAssessment?.id || '',
        scoreUpdateRequest: {
          assessment_id: selectedAssessment?.id || '',
          activity_id: values.activityId,
          score_value: values.score,
          comment: values.comment ? [values.comment] : null,
          evidence: values.evidence ? [values.evidence] : null,
        }
      });

      enqueueSnackbar('Score updated successfully', { variant: 'success' });
      resetForm();
    } catch (error) {
      console.error('Error updating score:', error);
      enqueueSnackbar('Failed to update score. Please try again.', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActivityChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    handleChange: any,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>,
  ) => {
    const selectedActivityId = e.target.value;
    handleChange(e);

    if (selectedActivityId && activityIdMappings[selectedActivityId]) {
      const scoreData = activityIdMappings[selectedActivityId];

      void setFieldValue('score', scoreData.score);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      fullScreen
      aria-labelledby='dialog-title'
      aria-describedby='dialog-content'
    >
      <UIDialogTitle id='dialog-title' title={'Update Scores'} onCloseBtnAction={handleClose} />
      <DialogContent id='dialog-content'>
        {isScoresLoading ? (
          <Loader fullscreen />
        ) : (
          <Grid container spacing={2}>
            <Formik
              initialValues={initialFormValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              validateOnChange
              enableReinitialize
            >
              {({ handleChange, isSubmitting: formikSubmitting, errors, touched, setFieldValue }) => {
                const isFormDisabled = isScoresLoading || isSubmitting || formikSubmitting || isActionLoading;

                return (
                  <>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Form>
                        <Stack gap='2rem'>
                          <SelectFormField
                            required
                            name='activityId'
                            label='Activity ID'
                            fullWidth
                            variant='outlined'
                            options={activityIdOptions}
                            helperText={
                              touched.activityId && errors.activityId ? (
                                <ErrorMessage name='activityId' />
                              ) : isScoresError ? (
                                'Failed to load activity IDs'
                              ) : ''
                            }
                            error={Boolean(touched.activityId && errors.activityId)}
                            disabled={isFormDisabled}
                            isLoading={isScoresLoading}
                            isErrorIcon={isScoresError}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleActivityChange(
                              e,
                              handleChange,
                              setFieldValue,
                            )}
                          />

                          <Field
                            as={TextField}
                            name='score'
                            required
                            fullWidth
                            label='Score'
                            type='number'
                            variant='outlined'
                            helperText={<ErrorMessage name='score' />}
                            error={Boolean(touched.score && errors.score)}
                            disabled={isFormDisabled}
                          />
                          <Field
                            as={TextField}
                            name='comment'
                            label='Comment'
                            multiline
                            rows={3}
                            fullWidth
                            variant='outlined'
                            helperText={<ErrorMessage name='comment' />}
                            error={Boolean(touched.comment && errors.comment)}
                            disabled={isFormDisabled}
                          />

                          <Field
                            as={TextField}
                            name='evidence'
                            label='Evidence'
                            multiline
                            rows={3}
                            fullWidth
                            variant='outlined'
                            helperText={<ErrorMessage name='evidence' />}
                            error={Boolean(touched.evidence && errors.evidence)}
                            disabled={isFormDisabled}
                          />

                          <Button
                            type='submit'
                            variant='contained'
                            color='primary'
                            disabled={isFormDisabled}
                          >
                            Update Score
                          </Button>
                        </Stack>
                      </Form>
                    </Grid>

                    <Grid size={{ xs: 12, md: 8 }}>
                      <ActivityTable data={scoresArray} loading={isScoresLoading} setFieldValue={setFieldValue} />
                    </Grid>
                  </>
                );
              }}
            </Formik>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ScoreUpdateModal;